import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import logo from "../assets/logo.webp";
import appstore from "../assets/appstore.webp";
import googleplay from "../assets/googleplay.webp";
import {
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaXTwitter,
  FaLinkedin,
} from "react-icons/fa6";
import { AiFillTikTok } from "react-icons/ai";
import { RefreshCw } from "lucide-react";
import BASE_URL from "../utils";
import { useToast } from "./Toaster";

export const socialLinks = [
  {
    href: "https://www.facebook.com/NafsikasGarden/",
    Icon: FaFacebookF,
    label: "Facebook",
  },
  {
    href: "https://www.instagram.com/nafsikasgarden/",
    Icon: FaInstagram,
    label: "Instagram",
  },
  {
    href: "https://youtube.com/playlist?list=PL3JaWsdDS9V-cQkwIyUFppOkEDAAORIeg",
    Icon: FaYoutube,
    label: "YouTube",
  },
  // { href: "https://x.com/nafsikasgarden", Icon: FaXTwitter, label: "X" },
  {
    href: "https://www.linkedin.com/company/nafsikas-garden/posts/?viewAsMember=true",
    Icon: FaLinkedin,
    label: "LinkedIn",
  },
  {
    href: "https://www.tiktok.com/@nafsikasgarden",
    Icon: AiFillTikTok,
    label: "TikTok",
  },
];

/* -------------------------------------------
   ✅ Auto-detect API URL
-------------------------------------------- */

const Footer = () => {
  const toast = useToast();
  const [footerMenu, setFooterMenu] = useState([]);
  const [customerServiceMenu, setCustomerServiceMenu] = useState([]);
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const captchaInputRef = useRef(null);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 5; i++)
      code += chars[Math.floor(Math.random() * chars.length)];
    setCaptcha(code);
    setCaptchaInput("");
  };

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const [footerRes, serviceRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/menus/handle/footer`),
          axios.get(`${BASE_URL}/api/menus/handle/customer-service`),
        ]);
        setFooterMenu(footerRes.data.items || []);
        setCustomerServiceMenu(serviceRes.data.items || []);
      } catch (err) {
        console.error("Error loading footer menus:", err);
      }
    };
    fetchMenus();
  }, []);

  useEffect(() => {
    if (showCaptcha) captchaInputRef.current?.focus();
  }, [showCaptcha]);

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleSubscribeClick = () => {
    const trimmed = email.trim();
    if (!trimmed) return toast.error("Please enter an email");
    if (!isValidEmail(trimmed))
      return toast.error("Please enter a valid email");
    generateCaptcha();
    setShowCaptcha(true);
  };

  const submitNewsletter = async (captchaValue) => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    setSubmitting(true);

    try {
      await axios.post(`${BASE_URL}/api/newsletter/subscribe`, {
        email: email.trim(),
      });
      toast.success("Subscribed successfully!");
      setEmail("");
      setShowCaptcha(false);
      setCaptchaInput("");
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Subscription failed";
      console.error("Newsletter subscribe failed:", err.response?.data || err);
      toast.error(msg);
      generateCaptcha();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaInput(value);
    if (value.trim().toLowerCase() === captcha.toLowerCase()) {
      submitNewsletter(value);
    }
  };

  return (
    <footer className="bg-[#FAF5F7] pt-16 pb-10">
      {/* 
        Layout:
        - < md: 1 column (stacked)
        - md: 3 columns (logo + quick + customer), newsletter row below with max-width, right-aligned
        - lg+: 4 columns, newsletter is the 4th column
      */}
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-10 text-gray-700">
        {/* Left Logo + Tagline */}
        <div>
          <img src={logo} alt="Logo" className="h-12 w-auto mb-3" />
          <p className="text-[#A83288] font-semibold text-sm">
            "Welcome to the Garden."
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="font-semibold text-lg mb-3">Quick Links</h3>
          <ul className="space-y-2">
            {footerMenu.map((item) => (
              <li key={item._id}>
                <a
                  href={item.url}
                  className="hover:text-[#C03CA0] transition-colors"
                >
                  {item.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Customer Service */}
        <div>
          <h3 className="font-semibold text-lg mb-3">Customer service</h3>
          <ul className="space-y-2">
            {customerServiceMenu.map((item) => (
              <li key={item._id}>
                <a
                  href={item.url}
                  className="hover:text-[#C03CA0] transition-colors"
                >
                  {item.title}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter + Social + Apps */}
        <div className="md:col-span-3 lg:col-span-1 flex lg:justify-start">
          {/* Inner wrapper with max-width so newsletter isn’t full-width */}
          <div className="w-full max-w-md">
            {/* Newsletter */}
            <h3 className="font-semibold text-lg mb-3">
              Get Delicious Recipes & Deals
            </h3>

            <div className="w-full mb-6 space-y-2">
              {!showCaptcha ? (
                <>
                  <input
                    type="email"
                    value={email}
                    placeholder="Email"
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSubscribeClick();
                    }}
                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-full 
                               text-gray-700 outline-none placeholder-gray-500"
                  />
                  <button
                    type="button"
                    onClick={handleSubscribeClick}
                    className="w-full bg-[#C03CA0] hover:bg-[#a83288] text-white px-6 py-2.5 
                               rounded-full text-sm font-semibold transition-colors"
                  >
                    Subscribe
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 px-4 py-2.5 rounded-full font-mono text-sm tracking-[0.28em] text-center bg-white border border-[#C03CA0] text-[#C03CA0] font-semibold select-none">
                      {captcha}
                    </div>
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      disabled={submitting}
                      className="shrink-0 w-11 h-11 rounded-full border border-[#C03CA0] text-[#C03CA0] flex items-center justify-center hover:bg-[#C03CA0] hover:text-white transition disabled:opacity-60"
                      aria-label="Refresh captcha"
                    >
                      <RefreshCw size={16} />
                    </button>
                  </div>
                  <input
                    ref={captchaInputRef}
                    type="text"
                    value={captchaInput}
                    placeholder="Enter captcha"
                    onChange={(e) => handleCaptchaChange(e.target.value)}
                    disabled={submitting}
                    autoComplete="off"
                    className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-full 
                               text-gray-700 outline-none placeholder-gray-500 disabled:opacity-60"
                  />
                  {submitting && (
                    <p className="text-xs text-[#C03CA0] text-center">
                      Sending...
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Social Icons */}
            <h3 className="font-semibold text-lg mb-3">Let's Connect</h3>

            <div className="flex items-center gap-5 mb-5">
              {socialLinks.map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  className="text-gray-700 hover:text-[#C03CA0] text-xl"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                >
                  <Icon />
                </a>
              ))}
            </div>

            {/* App Buttons */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="https://apps.apple.com/in/app/the-plant-based-switch/id6444078958"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={appstore}
                  alt="App Store"
                  className="h-10 w-auto hover:scale-105 transition-transform"
                />
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.plant.based.food&pli=1"
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={googleplay}
                  alt="Google Play"
                  className="h-10 w-auto hover:scale-105 transition-transform"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200 mt-10 pt-6 text-center text-sm text-gray-600">
        © {new Date().getFullYear()}, Nafsika. All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import logo from "../assets/logo.webp";
// import appstore from "../assets/appstore.webp";
// import googleplay from "../assets/googleplay.webp";
// import {
//   FaFacebookF,
//   FaInstagram,
//   FaYoutube,
//   FaXTwitter,
// } from "react-icons/fa6";

// /* -------------------------------------------
//    ✅ Auto-detect API URL
// -------------------------------------------- */
// let BASE_URL = "";
// if (process.env.REACT_APP_API_URL) {
//   BASE_URL = process.env.REACT_APP_API_URL;
// } else if (window.location.hostname === "localhost") {
//   BASE_URL = "https://admin.naf.network";
// } else {
//   BASE_URL = "https://admin.naf.network";
// }

// const Footer = () => {
//   const [footerMenu, setFooterMenu] = useState([]);
//   const [customerServiceMenu, setCustomerServiceMenu] = useState([]);
//   const [email, setEmail] = useState("");
//   const [submitting, setSubmitting] = useState(false);

//   useEffect(() => {
//     const fetchMenus = async () => {
//       try {
//         const [footerRes, serviceRes] = await Promise.all([
//           axios.get(`${BASE_URL}/api/menus/handle/footer`),
//           axios.get(`${BASE_URL}/api/menus/handle/customer-service`),
//         ]);
//         setFooterMenu(footerRes.data.items || []);
//         setCustomerServiceMenu(serviceRes.data.items || []);
//       } catch (err) {
//         console.error("Error loading footer menus:", err);
//       }
//     };
//     fetchMenus();
//   }, []);

//   const handleSubscribe = async () => {
//     if (!email) return alert("Please enter an email");
//     setSubmitting(true);

//     try {
//       await axios.post(`${BASE_URL}/api/newsletter/subscribe`, { email });
//       alert("Subscribed successfully!");
//       setEmail("");
//     } catch (err) {
//       alert("Subscription failed");
//     }
//     setSubmitting(false);
//   };

//   return (
//     <footer className="bg-[#FAF5F7] pt-16 pb-10">
//       <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 text-gray-700">

//         {/* Left Logo + Tagline */}
//         <div>
//           <img src={logo} alt="Logo" className="h-12 w-auto mb-3" />
//           <p className="text-[#C03CA0] font-semibold text-sm">
//             "Welcome to the Garden."
//           </p>
//         </div>

//         {/* Quick Links */}
//         <div>
//           <h3 className="font-semibold text-lg mb-3">Quick Links</h3>
//           <ul className="space-y-2">
//             {footerMenu.map((item) => (
//               <li key={item._id}>
//                 <a
//                   href={item.url}
//                   className="hover:text-[#C03CA0] transition-colors"
//                 >
//                   {item.title}
//                 </a>
//               </li>
//             ))}
//           </ul>
//         </div>

//         {/* Customer Service */}
//         <div>
//           <h3 className="font-semibold text-lg mb-3">Customer service</h3>
//           <ul className="space-y-2">
//             {customerServiceMenu.map((item) => (
//               <li key={item._id}>
//                 <a
//                   href={item.url}
//                   className="hover:text-[#C03CA0] transition-colors"
//                 >
//                   {item.title}
//                 </a>
//               </li>
//             ))}
//           </ul>
//         </div>

//         {/* Newsletter + Social + Apps */}
//         <div>

//           {/* Newsletter */}
//           {/* Newsletter */}
// <h3 className="font-semibold text-lg mb-3">
//   Get Delicious Recipes & Deals
// </h3>

// <div className="w-full mb-6">
//   <div className="flex w-full">

//     {/* Email Input */}
//     <input
//       type="email"
//       value={email}
//       placeholder="Email"
//       onChange={(e) => setEmail(e.target.value)}
//       className="flex-1 px-4 py-2.5 bg-white border border-gray-300 border-r-0 rounded-l-full
//                  text-gray-700 outline-none placeholder-gray-500"
//     />

//     {/* Subscribe Button */}
//     <button
//       onClick={handleSubscribe}
//       disabled={submitting}
//       className="bg-[#00ADBB] hover:bg-[#0095A2] text-white px-6 py-2.5
//                  rounded-r-full border border-gray-300 border-l-0
//                  text-sm font-semibold whitespace-nowrap transition-colors"
//     >
//       {submitting ? "Sending..." : "Subscribe"}
//     </button>

//   </div>
// </div>

//           {/* Social Icons */}
//           <h3 className="font-semibold text-lg mb-3">Let's Connect</h3>

//           <div className="flex items-center gap-5 mb-5">
//             <a
//               href="https://www.facebook.com/NafsikasGarden/"
//               className="text-gray-700 hover:text-[#C03CA0] text-xl"
//             >
//               <FaFacebookF />
//             </a>

//             <a
//               href="https://www.instagram.com/nafsikasgarden/"
//               className="text-gray-700 hover:text-[#C03CA0] text-xl"
//             >
//               <FaInstagram />
//             </a>

//             <a
//               href="#"
//               className="text-gray-700 hover:text-[#C03CA0] text-xl"
//             >
//               <FaYoutube />
//             </a>

//             <a
//               href="#"
//               className="text-gray-700 hover:text-[#C03CA0] text-xl"
//             >
//               <FaXTwitter />
//             </a>
//           </div>

//           {/* App Buttons */}
//           <div className="flex flex-row items-start sm:items-center gap-4">
//             <a
//               href="https://apps.apple.com/in/app/the-plant-based-switch/id6444078958"
//               target="_blank"
//             >
//               <img
//                 src={appstore}
//                 alt="App Store"
//                 className="h-10 w-auto hover:scale-105 transition-transform"
//               />
//             </a>

//             <a
//               href="https://play.google.com/store/apps/details?id=com.plant.based.food&pli=1"
//               target="_blank"
//             >
//               <img
//                 src={googleplay}
//                 alt="Google Play"
//                 className="h-10 w-auto hover:scale-105 transition-transform"
//               />
//             </a>
//           </div>
//         </div>
//       </div>

//       {/* Bottom Bar */}
//       <div className="border-t border-gray-200 mt-10 pt-6 text-center text-sm text-gray-500">
//         © {new Date().getFullYear()}, Nafsika. All rights reserved.
//       </div>
//     </footer>
//   );
// };

// export default Footer;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import logo from "../assets/logo.webp";
// import appstore from "../assets/appstore.webp";
// import googleplay from "../assets/googleplay.webp";
// import {
//   FaFacebookF,
//   FaInstagram,
//   FaYoutube,
//   FaXTwitter,
// } from "react-icons/fa6";

// /* -------------------------------------------
//    ✅ Auto-detect API URL
// -------------------------------------------- */
// let BASE_URL = "";
// if (process.env.REACT_APP_API_URL) {
//   BASE_URL = process.env.REACT_APP_API_URL;
// } else if (window.location.hostname === "localhost") {
//   BASE_URL = "https://admin.naf.network";
// } else {
//   BASE_URL = "https://admin.naf.network";
// }

// const Footer = () => {
//   const [footerMenu, setFooterMenu] = useState([]);
//   const [customerServiceMenu, setCustomerServiceMenu] = useState([]);

//   useEffect(() => {
//     const fetchMenus = async () => {
//       try {
//         const [footerRes, serviceRes] = await Promise.all([
//           axios.get(`${BASE_URL}/api/menus/handle/footer`),
//           axios.get(`${BASE_URL}/api/menus/handle/customer-service`),
//         ]);
//         setFooterMenu(footerRes.data.items || []);
//         setCustomerServiceMenu(serviceRes.data.items || []);
//       } catch (err) {
//         console.error("Error loading footer menus:", err);
//       }
//     };
//     fetchMenus();
//   }, []);

//   return (
//     <footer className="bg-[#FAF5F7] pt-16 pb-8">
//       <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-8 text-gray-700">

//         {/* Left Logo + Tagline */}
//         <div className="md:col-span-1">
//           <img src={logo} alt="Logo" className="h-12 w-auto mb-3" />
//           <p className="text-[#C03CA0] font-semibold text-sm">
//             "Welcome to the Garden."
//           </p>
//         </div>

//         {/* Quick Links */}
//         <div>
//           <h3 className="font-semibold text-lg mb-3">Quick Links</h3>
//           <ul className="space-y-2">
//             {footerMenu.map((item) => (
//               <li key={item._id}>
//                 <a
//                   href={item.url}
//                   className="hover:text-[#C03CA0] transition-colors"
//                 >
//                   {item.title}
//                 </a>
//               </li>
//             ))}
//           </ul>
//         </div>

//         {/* Customer Service */}
//         <div>
//           <h3 className="font-semibold text-lg mb-3">Customer service</h3>
//           <ul className="space-y-2">
//             {customerServiceMenu.map((item) => (
//               <li key={item._id}>
//                 <a
//                   href={item.url}
//                   className="hover:text-[#C03CA0] transition-colors"
//                 >
//                   {item.title}
//                 </a>
//               </li>
//             ))}
//           </ul>
//         </div>

//         {/* Social + App Links */}
//         <div>
//             <h3 className="font-semibold text-lg mb-3">Let's Connect</h3>

//             {/* Social Icons */}
//             <div className="flex items-center gap-4 mb-5">
//                 <a
//                 href="#"
//                 className="text-gray-700 hover:text-[#C03CA0] transition-colors text-lg"
//                 aria-label="Facebook"
//                 >
//                 <FaFacebookF />
//                 </a>
//                 <a
//                 href="#"
//                 className="text-gray-700 hover:text-[#C03CA0] transition-colors text-lg"
//                 aria-label="Instagram"
//                 >
//                 <FaInstagram />
//                 </a>
//                 <a
//                 href="#"
//                 className="text-gray-700 hover:text-[#C03CA0] transition-colors text-lg"
//                 aria-label="YouTube"
//                 >
//                 <FaYoutube />
//                 </a>
//                 <a
//                 href="#"
//                 className="text-gray-700 hover:text-[#C03CA0] transition-colors text-lg"
//                 aria-label="Twitter / X"
//                 >
//                 <FaXTwitter />
//                 </a>
//             </div>

//             {/* App Store Buttons */}
//             <div className="flex flex-col sm:flex-row gap-3">
//                 <a href="https://apps.apple.com/in/app/the-plant-based-switch/id6444078958" target="_blank" rel="noopener noreferrer">
//                 <img
//                     src={appstore}
//                     alt="App Store"
//                     className="h-10 w-auto hover:scale-105 transition-transform"
//                 />
//                 </a>
//                 <a href="https://play.google.com/store/apps/details?id=com.plant.based.food&pli=1" target="_blank" rel="noopener noreferrer">
//                 <img
//                     src={googleplay}
//                     alt="Google Play"
//                     className="h-10 w-auto hover:scale-105 transition-transform"
//                 />
//                 </a>
//             </div>
//             </div>
//       </div>

//       {/* Bottom Bar */}
//       <div className="border-t border-gray-200 mt-10 pt-6 text-center text-sm text-gray-500">
//         © {new Date().getFullYear()}, Nafsika. All rights reserved.
//       </div>
//     </footer>
//   );
// };

// export default Footer;
